import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LoaderBar, Page } from 'src/components';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Table,
  makeStyles,
  colors,
  Container,
  Button
} from '@material-ui/core';
import InventoryToolbar from './component/AvailableInventoryToolbar';
import { slices, useAppDispatch } from 'src/redux';
import { InventoryTableBody } from './component/AvailableInventoryTableBody';
import AvailableInventoryFilter from './component/AvailableInventoryFilter';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  AvailableInventoryItemData,
  AvailableInventorySumData
} from 'src/types/available-inventory';
import { InventoryTableHeader } from './component';
import { Pagination } from '@material-ui/lab';
import { usePermissions, useSnackBar } from 'src/hooks';
import useUserInfo from 'src/hooks/user/use-user-info';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { PaginationV2 } from 'src/types';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import GetAppIcon from '@material-ui/icons/GetApp';
import { dateToday, downloadCSV } from 'src/utils';

const { actions: inventoryActions } = slices.inventory;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  inventoryCard: {
    height: '100%'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  tableRow: {
    height: 35,
    padding: 10
  },
  priceInput: {
    height: 30
  },
  lightGrey: {
    backgroundColor: colors.blueGrey[50]
  },
  retailPriceCell: {
    width: 200
  },
  columnBtn: {
    marginTop: theme.spacing(2),
    justifyContent: 'flex-end',
    display: 'flex'
  },
  toCsvText: {
    color: theme.palette.primary.main
  },
  toCsvBtn: {
    marginLeft: theme.spacing(2)
  }
}));

const AvailableInventoryView = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { canSeeAvailableInventory } = usePermissions();
  const { userBranchOptions } = useUserInfo();
  const { selectedBranchIDs, setSelectedBranchIDs } = useBranchInfo();
  const snackBar = useSnackBar();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState<{
    keyword?: string;
    fromDate?: string;
    toDate?: string;
    category_id?: number;
    page_num?: number;
    excluded_manufacturer_ids?: number[];
  }>({
    keyword: '',
    fromDate: '',
    toDate: '',
    category_id: undefined,
    page_num: 1,
    excluded_manufacturer_ids: []
  });

  const [availableInventorySum, setAvailableInventorySum] = useState<
    AvailableInventorySumData
  >();

  const [pageDataList, setPageDataList] = useState<
    AvailableInventoryItemData[]
  >();
  const [page_meta, setPageMeta] = useState<PaginationV2>();

  const queryParams = useMemo(() => {
    const query = Object.fromEntries(searchParams.entries());
    return query;
  }, [searchParams]);

  const getInventory = useCallback(
    async (params?: any) => {
      if (!canSeeAvailableInventory) return;

      setIsLoading(true);
      setPageDataList(undefined);

      try {
        const sumResponse = unwrapResult(
          await dispatch(
            inventoryActions.getAvailableInventorySumThunk({
              keyword: params?.keyword,
              start_date: params?.start_date,
              end_date: params?.end_date,
              branch_ids: params?.branch_ids?.split(',').map(Number),
              category_id: Number(params?.category_id),
              excluded_manufacturer_ids: params?.excluded_manufacturer_ids
                ?.split(',')
                .map(Number),
              page: params?.page ? Number(params?.page) : 1
            })
          )
        );

        setAvailableInventorySum(sumResponse?.originalData?.data);

        if (sumResponse?.success) {
          const listResponse = unwrapResult(
            await dispatch(
              inventoryActions.getAvailableInventoryListV2Thunk({
                keyword: params?.keyword,
                start_date: params?.start_date,
                end_date: params?.end_date,
                to_get: 'list',
                branch_ids: params?.branch_ids?.split(',').map(Number),
                category_id: Number(params?.category_id),
                excluded_manufacturer_ids: params?.excluded_manufacturer_ids
                  ?.split(',')
                  .map(Number),
                page: params?.page ? Number(params?.page) : 1
              })
            )
          );

          if (listResponse?.success) {
            setPageDataList(listResponse.originalData?.data);
            setPageMeta(listResponse?.originalData?.meta);

            setFilters((prev) => ({
              ...prev,
              keyword: params?.keyword,
              fromDate: params?.start_date,
              toDate: params?.end_date,
              category_id: params?.category_id,
              page_num: Number(params?.page) || 1
            }));

            setSelectedBranchIDs(params?.branch_ids?.split(',').map(Number));
          } else {
            const error_keys = Object.keys(listResponse?.errors);
            snackBar.show({
              severity: 'error',
              message: listResponse.errors[error_keys[0] && error_keys[0]]
            });
          }
        } else {
          const error_keys = Object.keys(sumResponse.errors || {});
          snackBar.show({
            severity: 'error',
            message:
              sumResponse.errors?.[error_keys[0]] ||
              'An unknown error occurred.'
          });
        }
      } catch (error) {
        console.error('Error fetching available inventory:', error);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canSeeAvailableInventory, dispatch, setSelectedBranchIDs]
  );

  const onApplyQuery = useCallback(
    (page_num?: number) => {
      const params = new URLSearchParams({
        start_date: filters.fromDate || '',
        end_date: filters.toDate || '',
        to_get: 'list',
        branch_ids: selectedBranchIDs.join(','),
        category_id: filters.category_id?.toString() || '',
        page: page_num?.toString() || '1',
        excluded_manufacturer_ids:
          filters?.excluded_manufacturer_ids?.join(',') || ''
      });

      // Check if keyword is not empty or undefined
      if (filters?.keyword) {
        params.set('keyword', filters?.keyword);
      }
      if (
        filters?.excluded_manufacturer_ids &&
        filters?.excluded_manufacturer_ids?.length <= 0
      ) {
        params.delete('excluded_manufacturer_ids');
      }

      navigate(`?${params}`);
    },
    [filters, navigate, selectedBranchIDs]
  );

  const onHandlePageChange = (page_num?: number) => {
    onApplyQuery(page_num);
    setFilters((prev) => ({ ...prev, page_num }));
  };

  const onHandleExportCsv = async (params: any) => {
    try {
      const listResponse = unwrapResult(
        await dispatch(
          inventoryActions.getAvailableInventoryListV2Thunk({
            keyword: params?.keyword,
            start_date: params?.start_date,
            end_date: params?.end_date,
            to_get: 'list',
            branch_ids: params?.branch_ids?.split(',').map(Number),
            category_id: Number(params?.category_id),
            excluded_manufacturer_ids: params?.excluded_manufacturer_ids
              ?.split(',')
              .map(Number),
            limit: 99999
          })
        )
      );

      if (listResponse?.success) {
        const csvName = `Available_Inventory ${dateToday()}.csv`;
        const structured_list = listResponse?.originalData?.data?.map(
          (csv) => ({
            product_name: csv?.product_name,
            serial_no: csv?.serial_no,
            sku: csv?.sku,
            manufacturer: csv?.manufacturer,
            category_name: csv?.category_name,
            branch_name: csv?.branch_name,
            created_at: csv?.created_at,
            dealers_price: csv?.dealers_price,
            retail_price: csv?.retail_price,
            status: csv?.status,
            purchased_date: csv?.purchased_date,
            is_purchased: csv?.is_purchased
          })
        );

        downloadCSV(structured_list, csvName);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (queryParams && !isEmpty(queryParams)) {
      getInventory(queryParams);
    }
  }, [getInventory, queryParams]);

  return (
    <Page className={classes.root} title="Available Inventory">
      <Container maxWidth={false}>
        <AvailableInventoryFilter
          fromDate={filters.fromDate}
          toDate={filters.toDate}
          onChangeFromDate={(date) =>
            setFilters((prev) => ({ ...prev, fromDate: date }))
          }
          onChangeToDate={(date) =>
            setFilters((prev) => ({ ...prev, toDate: date }))
          }
          onHandleCategoryChange={(category_id?: number) =>
            setFilters((prev) => ({ ...prev, category_id }))
          }
          onChangeKeyword={(keyword?: string) =>
            setFilters((prev) => ({ ...prev, keyword }))
          }
          onHandleExcludedManufacturer={(
            excluded_manufacturer_ids?: number[]
          ) => setFilters((prev) => ({ ...prev, excluded_manufacturer_ids }))}
          keyword={filters?.keyword}
          onApplyFilterPress={onApplyQuery}
          userBranchOptions={userBranchOptions}
          isLoading={isLoading}
          selectedBranchIDs={selectedBranchIDs}
          setSelectedBranchIDs={setSelectedBranchIDs}
        />

        <InventoryToolbar
          sumAvailable={availableInventorySum}
          loaderBar={isLoading}
        />

        <Box mt={2}>
          <LoaderBar isLoading={isLoading} />
          {pageDataList && pageDataList?.length > 0 ? (
            <Box
              sx={{
                width: '100%',
                mt: 1,
                mb: 1,
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={() => onHandleExportCsv(queryParams)}
                disabled={isLoading}
              >
                Export CSV
              </Button>
            </Box>
          ) : null}
          <Card>
            <PerfectScrollbar>
              <Box minWidth={1050}>
                <Table size="small">
                  <InventoryTableHeader />
                  <InventoryTableBody inventoryItem={pageDataList || []} />
                </Table>
              </Box>
            </PerfectScrollbar>
          </Card>
        </Box>

        <Box display="flex" justifyContent="flex-end" p={2}>
          {!isLoading ? (
            <Pagination
              count={page_meta?.last_page || 1}
              page={page_meta?.current_page || 1}
              onChange={(e, p) => onHandlePageChange(p)}
            />
          ) : null}
        </Box>
      </Container>
    </Page>
  );
};

export default AvailableInventoryView;
